import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import {
  reduxForm,
  Field,
  Form,
  InjectedFormProps,
  getFormValues
} from 'redux-form';
import { compose } from 'ramda';
import api from 'api';
import { BoxWrapper, Button, Modal } from 'components/Base';
import Fields from 'components/Fields';
import Store from 'store/store';
import withMeta from 'hoc/withMeta';
import Router from 'components/Router/Router';
import CheckboxField from 'components/Fields/Checkbox/Checkbox';
import Dashboard from 'components/Layout/Dashboard/Dashboard';
import IPS from 'components/IPS/IPS';
import withCaseInfo from 'hoc/withCaseInfo';
import { Loader } from 'components/Base';
import { Notify } from 'utils';
import BottomNavBar from 'components/Cases/BottomNavBar';
import selectors from 'store/selectors';
import { CustomerType } from 'store/modules/Customers/types';
import { useAuthIo } from 'providers/AuthProvider/AuthIoProvider';
import './IPS.scss';
import ReportOptions from 'utils/ReportOptions';
import { useDispatch } from 'react-redux';
import {
  fetchDataIfNeeded,
  getExistingPolicies,
  getPolicies,
  getRiskProfiles
} from 'components/Cases/utils';

const { connect, actions } = Store;
const { useParams, useHistory } = Router;

const useStyles = makeStyles({
  summaryModal: {
    width: '800px',
    padding: '1rem',
    maxHeight: '80vh',
    overflowX: 'auto'
  },
  itemRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  }
});

interface InvestmentPSPageProps {
  getPastPerformance: any;
  customers: CustomerType[];
  formValues: any;
  caseInfo: any;
  setExecutiveSummary: any;
  recommendedPolicies: any;
  existingPolicies: any;
  setCapacityForLossConfig: Function;
}
const IPSPage = ({
  getPastPerformance,
  customers,
  formValues,
  caseInfo,
  setExecutiveSummary,
  recommendedPolicies,
  existingPolicies,
  setCapacityForLossConfig
}: InvestmentPSPageProps & InjectedFormProps) => {
  const dispatch = useDispatch();
  const isEvalue = caseInfo.riskProfiler === 'EValue';
  const [isLoading, setIsLoading] = useState(true);
  const [uploadToIo, setUploadToIo] = useState(false);
  const [isPastPerformanceLoading, setPastPerformanceLoading] = useState(true);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [isSummaryModelOpen, setIsSummaryModelOpen] = useState(false);

  const { portfolio } = useParams() as any;
  const history = useHistory();
  const { isAuthenticated } = useAuthIo();
  const isIoCustomer = !!customers.find(
    (customer: CustomerType) => !!customer.intelligent_office_id
  );

  const saveExecutiveSummary = () => {
    api
      .editCase({
        caseInfo: {
          ...caseInfo,
          executive_summary: formValues.executive_summary
        }
      })
      .then(() => {
        setExecutiveSummary(formValues.executive_summary);
        setIsSummaryModelOpen(false);
        Notify.success('Executive Summary', 'Executive Summary saved');
      })
      .catch(() => {
        Notify.error('Unknown error', 'Please contact administrator');
      });
  };

  const getAllData = () => {
    api
      .getCapacityForLossConfig()
      .then((response: any) => setCapacityForLossConfig(response.data));
    Promise.all([
      dispatch(getExistingPolicies(portfolio) as any),
      dispatch(getPolicies(portfolio) as any),
      dispatch(getRiskProfiles(portfolio) as any),
      ...(isEvalue
        ? [
            dispatch(
              fetchDataIfNeeded('portfolioSplit', () =>
                api.getPortfolioSplitData({ portfolio })
              ) as any
            )
          ]
        : []),
      dispatch(
        fetchDataIfNeeded('portfolioRiskScore', () =>
          api.getPortfolioRiskScore({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('existingPoliciesRiskScore', () =>
          api.getExistingPoliciesRiskScore({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('recommendedPoliciesRiskScore', () =>
          api.getRecommendedPoliciesRiskScore({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('existingPoliciesCharges', () =>
          api.getExsitinPoliciesChargesData({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('recommendedPoliciesCharges', () =>
          api.getRecommendedPoliciesChargesData({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('assetAllocations', () =>
          api.getAssetAllocationData({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('sectorBreakdown', () =>
          api.getSectorBreakdownData({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('regionlBreakdown', () =>
          api.getRegionalBreakdownData({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('countryBreakdown', () =>
          api.getCountryBreakdownData({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('recommendedRiskAnalysis', () =>
          api.getRiskAnalysisData({ portfolio })
        ) as any
      )
    ]).finally(() => setIsLoading(false));
  };

  useEffect(() => {
    setIsLoading(true);
    getAllData();

    getPastPerformance({ portfolio })
      .then(() => {
        setPastPerformanceLoading(false);
      })
      .catch(() => {
        Notify.error('Error while loading past performance data');
        setPastPerformanceLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const classes = useStyles();

  const [sections] = useState([
    {
      items: [
        'Risk analysis',
        isEvalue ? 'Typical asset allocation' : null,
        isEvalue ? 'Forecast investment returns' : null,
        isEvalue ? null : 'About your risk group',
        'Existing portfolio: Overview',
        'Existing portfolio: Analysis',
        'Recommended portfolio: Overview',
        'Recommended vs existing portfolio: Analysis',
        'Cost of advice'
      ].filter(Boolean)
    },
    {
      title: 'Model portfolio view:',
      items: ['Show underlying funds']
    },
    {
      title: 'Aggregated costs and charges projections:',
      items: ['Individual policies', 'Consolidated portfolio']
    }
  ]);

  const [reportSections, setReportSections] = useState([]);

  const selectedOptions = Object.values(reportSections)
    .filter((item: any) => item.isSelected)
    .map((item: any) => item.name);

  const printReportOptions = (type: string) => {
    history.push({
      pathname: `/${portfolio}/ips/print`,
      state: {
        downloadType: type,
        upload_to_io: uploadToIo,
        report_sections: selectedOptions
      }
    });
  };

  return (
    <Dashboard>
      <Loader isLoading={isLoading}>
        <div style={{ paddingBottom: '86px' }}>
          <IPS
            isPastPerformanceLoading={isPastPerformanceLoading}
            portfolio={portfolio}
          />
        </div>
        {recommendedPolicies.length > 0 && (
          <BottomNavBar
            rightButtons={[
              <>
                <Button
                  key="Add Executive Summary"
                  style={{ marginRight: '24px' }}
                  onClick={() => {
                    setIsSummaryModelOpen(true);
                  }}
                >
                  Add Executive Summary
                </Button>
                <Button
                  key="Download report"
                  onClick={() => {
                    setIsModelOpen(true);
                  }}
                >
                  Download report
                </Button>
              </>
            ]}
          />
        )}
      </Loader>
      <Modal isOpen={isModelOpen} closeModalFunc={() => setIsModelOpen(false)}>
        <BoxWrapper
          title="Portfolio Review report options"
          closeModal={() => setIsModelOpen(false)}
        >
          <div className="printModal">
            <Form>
              <ReportOptions
                sections={sections}
                recommendedPolicies={recommendedPolicies}
                existingPolicies={existingPolicies}
                setSelectedSections={setReportSections}
              />
              {isAuthenticated && (
                <div
                  style={{
                    marginTop: '1rem',
                    borderTop: ' 1px solid rgb(212, 212, 212)'
                  }}
                >
                  <div
                    className={classes.itemRow}
                    style={{ marginTop: '1rem', marginBottom: '1rem' }}
                  >
                    <div>Upload document to IO</div>
                    <CheckboxField
                      meta={{ touched: false, invalid: false, error: false }}
                      fullWidth={false}
                      input={{
                        value: uploadToIo,
                        onChange: () => setUploadToIo(!uploadToIo),
                        checked: uploadToIo
                      }}
                    />
                  </div>
                </div>
              )}
            </Form>
            {isIoCustomer && !isAuthenticated && (
              <div
                style={{
                  marginTop: '1rem',
                  borderTop: ' 1px solid rgb(212, 212, 212)'
                }}
              >
                <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                  If you would like to upload to IO you will need to return to
                  the cases page, click new case then click import to login in
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              <Button
                disabled={!selectedOptions.length}
                onClick={() => {
                  printReportOptions('pdf');
                }}
              >
                Download as PDF
              </Button>
              <Button
                disabled={!selectedOptions.length}
                key="Download as Word button"
                onClick={() => {
                  printReportOptions('word');
                }}
              >
                Download as Word
              </Button>
            </div>
          </div>
        </BoxWrapper>
      </Modal>
      <Modal
        isOpen={isSummaryModelOpen}
        closeModalFunc={() => setIsSummaryModelOpen(false)}
      >
        <BoxWrapper
          title="Add Executive Summary"
          closeModal={() => setIsSummaryModelOpen(false)}
        >
          <div className={classes.summaryModal}>
            <Form>
              <div
                style={{
                  maxHeight: '400px',
                  overflowX: 'auto',
                  marginBottom: '5rem'
                }}
              >
                <Field
                  name="executive_summary"
                  shrink
                  fullWidth
                  component={Fields.Text}
                  multiline
                  minRows={10}
                  label="Executive Summary"
                  tooltip="This will appear at the top of your report for your client to see."
                  autoComplete="off"
                />
              </div>
            </Form>
            <div
              style={{
                paddingTop: '1rem',
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                position: 'absolute',
                bottom: 0,
                marginLeft: '-1rem',
                padding: '1rem',
                backgroundColor: 'white',
                borderRadius: '25px'
              }}
            >
              <Button
                color="primary"
                onClick={() => {
                  setIsSummaryModelOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                key="save"
                onClick={() => {
                  saveExecutiveSummary();
                }}
              >
                Save
              </Button>
            </div>
          </div>
        </BoxWrapper>
      </Modal>
    </Dashboard>
  );
};

export default compose(
  connect(
    (state: any) => ({
      customers: selectors.customersSelectors.customers(state),
      caseInfo: selectors.caseSelectors.case(state),
      recommendedPolicies: selectors.caseSelectors.policies(state),
      existingPolicies: selectors.caseSelectors.existingPolicies(state),
      initialValues: {
        executive_summary: selectors.IPSSelectos.caseExecutiveSummary(state)
      },
      formValues: getFormValues('ipsPrint')(state)
    }),
    {
      getPastPerformance: actions.ipsActions.getPastPerformance,
      setCase: actions.caseActions.setCaseInfo,
      setExecutiveSummary: actions.caseActions.setExecutiveSummary,
      setCapacityForLossConfig: actions.capacityForLossActions.setConfig
    }
  ),
  reduxForm({
    form: 'ipsPrint',
    enableReinitialize: true
  }),
  withMeta,
  withCaseInfo
)(IPSPage);
